import { Group, Tooltip } from "@mantine/core";
import {
  IconCircleX,
  IconFilePlus,
  IconMail,
  IconMailCheck,
  IconMailExclamation
} from "@tabler/icons-react";
import { t } from "i18next";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { IconButton } from "../../../../../mantine/components/IconButton/IconButton";
import { InvoiceState, InvoiceType } from "../../../../../types/neWebApi.types";
import {
  EMAIL_ERROR_STATES,
  EMAIL_ERROR_TOOLTIP_KEYS,
  EMAIL_IN_PROGRESS_STATES
} from "../../PPAInvoicePage.constants";
import type { PPAInvoice, PPAInvoiceId } from "../../PPAInvoicePage.types";
import { getInvoiceStatus, STATUS_LABELS } from "../utils/getInvoiceStatus";

const ACTION_BUTTON_FLEX = "0 0 155px";
const VOID_BUTTON_FLEX = "0 0 auto";

function formatEmailTooltip(
  emails: Array<string>,
  isDelivered: boolean
): string {
  const prefix = isDelivered ? "Versendet an" : "Per E-Mail an";
  return `${prefix}: ${emails.join(", ")}`;
}

export function getButtonColumn(
  isGeneratingInvoice: (invoiceId: PPAInvoiceId) => boolean,
  isSendingInvoice: (invoiceId: PPAInvoiceId) => boolean,
  isVoidingInvoice: (invoiceId: PPAInvoiceId) => boolean,
  onGenerateInvoice: (invoiceId: PPAInvoiceId) => void,
  onSendInvoice: (invoiceId: PPAInvoiceId) => void,
  onVoidInvoice: (invoiceId: PPAInvoiceId) => void
): MRT_ColumnDef<PPAInvoice> {
  return {
    id: "invoice_buttons",
    accessorFn: (row) => {
      const status = getInvoiceStatus(
        row.state,
        row.invoice_type,
        row.email_delivery_state
      );
      return status ? STATUS_LABELS[status] : null;
    },
    header: "Status",
    size: 300,
    enableSorting: false,
    filterVariant: "multi-select",
    mantineFilterMultiSelectProps: {
      data: Object.values(STATUS_LABELS)
    },
    Cell: ({ row }) => {
      const { invoice_type, state, unique_identifier } = row.original;
      const emails = row.original.seller_invoice_receiving_emails;
      const emailState = row.original.email_delivery_state;

      if (invoice_type === InvoiceType.Draft) {
        return (
          <Group gap="xs" wrap="nowrap">
            <IconButton
              color="primary"
              flex={ACTION_BUTTON_FLEX}
              icon={IconFilePlus}
              justify="flex-start"
              loading={isGeneratingInvoice(unique_identifier)}
              size="xs"
              td="underline"
              variant="transparent"
              onClick={() => onGenerateInvoice(unique_identifier)}
            >
              Erstellen
            </IconButton>
          </Group>
        );
      }

      if (state === InvoiceState.corrected) {
        return null;
      }

      const hasEmails = emails.length > 0;

      if (state === InvoiceState.ready) {
        if (emailState && EMAIL_IN_PROGRESS_STATES.has(emailState)) {
          return (
            <Group gap="xs" wrap="nowrap">
              <Tooltip label="Wird gerade versendet">
                <IconButton
                  color="primary"
                  disabled
                  flex={ACTION_BUTTON_FLEX}
                  icon={IconMail}
                  justify="flex-start"
                  size="xs"
                  variant="transparent"
                >
                  Wird versendet...
                </IconButton>
              </Tooltip>
              <VoidButton disabled invoiceType={invoice_type} />
            </Group>
          );
        }

        if (emailState && EMAIL_ERROR_STATES.has(emailState)) {
          return (
            <Group gap="xs" wrap="nowrap">
              <Tooltip
                label={t(EMAIL_ERROR_TOOLTIP_KEYS[emailState] ?? "")}
                multiline
                w={350}
              >
                <IconButton
                  color="error"
                  flex={ACTION_BUTTON_FLEX}
                  icon={IconMailExclamation}
                  justify="flex-start"
                  loading={isSendingInvoice(unique_identifier)}
                  size="xs"
                  td="underline"
                  variant="transparent"
                  onClick={() => onSendInvoice(unique_identifier)}
                >
                  Erneut senden
                </IconButton>
              </Tooltip>
              <VoidButton disabled invoiceType={invoice_type} />
            </Group>
          );
        }

        return (
          <Group gap="xs" wrap="nowrap">
            <Tooltip
              label={
                hasEmails
                  ? formatEmailTooltip(emails, false)
                  : "Keine E-Mail-Empfänger hinterlegt"
              }
            >
              <IconButton
                color="primary"
                disabled={!hasEmails}
                flex={ACTION_BUTTON_FLEX}
                icon={IconMail}
                justify="flex-start"
                loading={isSendingInvoice(unique_identifier)}
                size="xs"
                td="underline"
                variant="transparent"
                onClick={() => onSendInvoice(unique_identifier)}
              >
                Versenden
              </IconButton>
            </Tooltip>
            <VoidButton disabled invoiceType={invoice_type} />
          </Group>
        );
      }

      if (state === InvoiceState.delivered) {
        return (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label={formatEmailTooltip(emails, true)}>
              <IconButton
                color="success"
                disabled
                flex={ACTION_BUTTON_FLEX}
                icon={IconMailCheck}
                justify="flex-start"
                size="xs"
                variant="transparent"
              >
                Versendet
              </IconButton>
            </Tooltip>
            <VoidButton
              disabled={false}
              invoiceType={invoice_type}
              loading={isVoidingInvoice(unique_identifier)}
              onClick={() => onVoidInvoice(unique_identifier)}
            />
          </Group>
        );
      }

      return null;
    }
  };
}

function VoidButton({
  disabled,
  invoiceType,
  loading,
  onClick
}: {
  disabled: boolean;
  invoiceType: InvoiceType;
  loading?: boolean;
  onClick?: () => void;
}) {
  if (invoiceType !== InvoiceType.Invoice) {
    return null;
  }

  return (
    <IconButton
      color="error"
      disabled={disabled}
      flex={VOID_BUTTON_FLEX}
      icon={IconCircleX}
      loading={loading}
      size="xs"
      td={disabled ? undefined : "underline"}
      variant="transparent"
      onClick={onClick}
    >
      Stornieren
    </IconButton>
  );
}
